import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * Finance-only Management dashboards: who is on the finance list, the
 * Stripe-settled FX rate, and the Treasury scenario document.
 *
 * Mirrors cleanse_api/api/platform/finance_dashboard.py. Everything but
 * `access/me` (any staff) and `fx` needs finance access.
 */

export interface FinanceAccessMe {
  allowed: boolean;
  email: string | null;
}

export interface FinanceMember {
  email: string;
  added_by: string;
  created_at: string;
}

export interface FinanceMembers {
  members: FinanceMember[];
}

export interface FinanceFx {
  /** CAD per 1 USD, the median Stripe settled recent charges at; null when Stripe can't be read. */
  fx: number | null;
  source: 'stripe' | null;
  generated_at: string;
}

/**
 * The shared Treasury scenario. `inputs` is the portal-owned document
 * (TreasuryDoc, schema 3), stored as-is. Revision 0 with nulls means
 * nobody has saved one yet.
 */
export interface TreasuryState {
  inputs: Record<string, unknown> | null;
  base_case: Record<string, unknown> | null;
  revision: number;
  updated_by: string | null;
  updated_at: string | null;
}

export interface TreasurySaveArgs {
  inputs: object;
  /** The revision this edit was made on; a stale one gets a 409. */
  revision: number;
}

/**
 * A save answers with the new state. It goes straight into the cache rather
 * than a refetch, so a remount never edits on a revision we've moved past.
 * Errors are the caller's (a 409 refetches).
 */
async function settled<T>(fulfilled: Promise<{ data: T }>): Promise<T | null> {
  try {
    return (await fulfilled).data;
  } catch {
    return null;
  }
}

export const adminFinanceApi = createApi({
  reducerPath: 'adminFinanceApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['FinanceAccess', 'Treasury'],
  endpoints: (builder) => ({
    getFinanceAccessMe: builder.query<FinanceAccessMe, void>({
      query: () => '/management/finance/access/me',
      providesTags: [{ type: 'FinanceAccess', id: 'ME' }],
    }),
    getFinanceAccess: builder.query<FinanceMembers, void>({
      query: () => '/management/finance/access',
      providesTags: [{ type: 'FinanceAccess', id: 'LIST' }],
    }),
    addFinanceMember: builder.mutation<FinanceMember, { email: string }>({
      query: (body) => ({
        url: '/management/finance/access',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'FinanceAccess', id: 'LIST' }],
    }),
    removeFinanceMember: builder.mutation<void, string>({
      query: (email) => ({
        url: `/management/finance/access/${encodeURIComponent(email)}`,
        method: 'DELETE',
      }),
      // Removing yourself also changes `me`.
      invalidatesTags: ['FinanceAccess'],
    }),
    getFinanceFx: builder.query<FinanceFx, void>({
      query: () => '/management/finance/fx',
    }),
    getTreasury: builder.query<TreasuryState, void>({
      query: () => '/management/finance/treasury',
      providesTags: ['Treasury'],
    }),
    saveTreasury: builder.mutation<TreasuryState, TreasurySaveArgs>({
      query: (body) => ({
        url: '/management/finance/treasury',
        method: 'PUT',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const data = await settled(queryFulfilled);
        if (data)
          dispatch(
            adminFinanceApi.util.upsertQueryData('getTreasury', undefined, data)
          );
      },
    }),
    /** Saves `inputs` and makes them the base case. */
    saveTreasuryBaseCase: builder.mutation<TreasuryState, TreasurySaveArgs>({
      query: (body) => ({
        url: '/management/finance/treasury/base-case',
        method: 'PUT',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const data = await settled(queryFulfilled);
        if (data)
          dispatch(
            adminFinanceApi.util.upsertQueryData('getTreasury', undefined, data)
          );
      },
    }),
    /** inputs := base case. 409 when there is none, or `revision` is stale. */
    resetTreasury: builder.mutation<TreasuryState, { revision: number }>({
      query: (body) => ({
        url: '/management/finance/treasury/reset',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const data = await settled(queryFulfilled);
        if (data)
          dispatch(
            adminFinanceApi.util.upsertQueryData('getTreasury', undefined, data)
          );
      },
    }),
  }),
});

export const {
  useGetFinanceAccessMeQuery,
  useGetFinanceAccessQuery,
  useAddFinanceMemberMutation,
  useRemoveFinanceMemberMutation,
  useGetFinanceFxQuery,
  useGetTreasuryQuery,
  useSaveTreasuryMutation,
  useSaveTreasuryBaseCaseMutation,
  useResetTreasuryMutation,
} = adminFinanceApi;
